import { useEffect, useState } from 'react'
import { type SelectOption, SelectWithLabel } from '../../Components/SelectWithLabel'
import { sendGet } from '../../hooks/use-fetch'
import { TextAreaWithLabel } from '../../Components/TextArea'
import { TextFieldWithLabel } from '../../Components/TextField'
import { toast } from 'react-toastify'
import { type CaseloadHistoryObject, type CaseloadInfo } from '../../core/types'
import { AcceptDenyModal } from '../../Components/Modal/AcceptDenyModal'
import { Box, Typography } from '@mui/material'
import { LabelWithToolTip } from '../../Components/Label/Index'
import ChipSelect from '../../Components/ChipSelect'
import { ServiceLogCard } from '../../Components/CareManagement/ServiceLogCard'
import { CheckBoxWithLabel } from '../../Components/CheckBox'

export interface RemoveFromCaseloadProps {
  childID: number
  childName: string
  onClose: () => void
  onSubmit: (action: CaseloadHistoryObject) => void
  visible: boolean
  caseloadInfo: CaseloadInfo | null
}

export function RemoveFromCaseload (props: RemoveFromCaseloadProps): JSX.Element {
  const [action, setAction] = useState<CaseloadHistoryObject>({
    id: 0,
    reasonId: null,
    reason: '',
    careSummary: '',
    otherReason: null,
    caregiverNames: props.caseloadInfo?.users ?? [],
    endDate: new Date(),
    isdName: props.caseloadInfo?.isdName ?? '',
    orgName: props.caseloadInfo?.orgName ?? '',
    startDate: new Date(),
    providedEducations: props.caseloadInfo?.providedEducations ?? [],
    serviceLogs: props.caseloadInfo?.serviceLogs ?? [],
    addressedMostImportant: false,
    connectedToReferral: false,
    needsMet: false,
    reasonNotMet: ''
  })
  const yesnoUnknown: Array<SelectOption<string>> = [{ id: '-1', name: 'I don\'t know' }, { id: '0', name: 'No' }, { id: '1', name: 'Yes' }]
  const allServices = ['Behavior', 'Child Development', 'Dental/teething', 'Nutrition/feeding', 'Parenting', 'Potty Training', 'Sleep', 'Other']
  const [reasonList, setReasonList] = useState<Array<SelectOption<number>>>([])
  const [showOtherReason, setShowOtherReason] = useState<boolean>(false)
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response } = await sendGet('/CareManagement/RemovalReasons', {})
      setReasonList(response)
    }

    void fetchData()
  }, [props.caseloadInfo?.orgId])
  useEffect(() => {
    if (!props.visible) {
      setAction({
        id: 0,
        reasonId: null,
        reason: '',
        careSummary: '',
        otherReason: null,
        caregiverNames: props.caseloadInfo?.users ?? [],
        endDate: new Date(),
        isdName: props.caseloadInfo?.isdName ?? '',
        orgName: props.caseloadInfo?.orgName ?? '',
        startDate: new Date(),
        providedEducations: props.caseloadInfo?.providedEducations ?? [],
        serviceLogs: props.caseloadInfo?.serviceLogs ?? [],
        addressedMostImportant: false,
        connectedToReferral: false,
        needsMet: false,
        reasonNotMet: ''
      })
    }
  }, [props.visible])
  const titleContent = <Box>
    <Typography variant='h5' fontWeight={'bold'}>End Care Management</Typography>
    <Typography fontWeight={'bold'} variant='h6'>{props.childName}</Typography>
    <Typography fontWeight={'normal'} fontSize={'15'}></Typography>
    <Box>
      {props.caseloadInfo?.orgName}
    </Box>
  </Box>

  const reasonChange = (e: any): void => {
    const value = parseInt(e.target.value)
    const rsn = reasonList.find((r) => r.id === value)

    setAction({ ...action, reasonId: value, reason: rsn?.name ?? '' })
    const isOther = (rsn?.name === 'Other')
    setShowOtherReason(isOther)
  }
  const connectedToReferralChange = (val: boolean): void => {
    setAction({ ...action, connectedToReferral: val })
  }
  const handleChange = (e: any): void => {
    setAction({ ...action, [e.target.name]: e.target.value })
  }

  const handleClose = (): void => {
    props.onClose()
  }

  const handleSubmit = async (): Promise<void> => {
    if (showOtherReason && action.otherReason === '') {
      toast.error('Please enter Other Removal Reason')
    } else {
      props.onSubmit(action)
    }
  }

  const content = <Box sx={{ minWidth: '100%' }}>
    <LabelWithToolTip
      isInline={false}
      required={false}
      name={''}
      labelText={'Reason'}
      toolTipText={'Enter the reason for removing this child from caseload management.'}
      sx={{
        whiteSpace: 'break-spaces',
        color: 'black',
        overflowWrap: 'break-word',
        fontWeight: '600',
        fontSize: '16'
      }}
    />
    <SelectWithLabel
      name='reasonId'
      label=''
      width='100%'
      value={action?.reasonId ?? ''}
      options={reasonList}
      onChange={reasonChange}
      labelSx={{
        fontWeight: '600',
        fontSize: '16'
      }}
    />

    <ChipSelect
      label='Education Provided'
      toolTipText='All educational resources provided in this case.'
      labelSx={{
        fontWeight: '600',
        fontSize: '16'
      }}
      options={allServices.map(s => {
        return { id: s, name: s }
      })}
      values={action.providedEducations}
      onChange={(newVals) => {
        const newSelectedValues = [...allServices].filter(o => newVals.some(v => v === o))
        const retVals = newSelectedValues.map(v => {
          return v
        })
        setAction({ ...action, providedEducations: retVals })
      }}
      name={'chipSelect'}
    />
    {showOtherReason && <TextFieldWithLabel
      label='Other Removal Reason'
      name='otherReason'
      dataTestId='otherReason'
      onChange={handleChange}
      value={action.otherReason}
      labelSx={{
        fontWeight: '600',
        fontSize: '16'
      }}

    />
    }
    <Box sx={{ pb: '10px' }}>
      <TextAreaWithLabel
        name='careSummary'
        dataTestId='careSummary'
        label='Care Summary'
        value={action.careSummary}
        onChange={handleChange}
        multiline
        toolTip='Briefly summarize the care and services the individual received.'
        labelSx={{
          fontWeight: '600',
          fontSize: '16'
        }}
      />
    </Box>
    <Box display='flex' flexDirection={'row'} gap={1}>
      <Box width={'50%'}>
        <SelectWithLabel labelSx={{
          fontWeight: '600',
          fontSize: '16'
        }} options={yesnoUnknown} label='Needs Met' value={action.needsMet.toString()} onChange={handleChange} name={'needsMet'} width='100%'
        />
      </Box>
      <Box width={'50%'}>
        <SelectWithLabel labelSx={{
          fontWeight: '600',
          fontSize: '16'
        }}
          options={yesnoUnknown} label='Addressed Most Important Matter' value={action.addressedMostImportant.toString()} onChange={handleChange}
          name={'addressedMostImportant'} width='100%'
        />
      </Box>
    </Box>
    <TextFieldWithLabel labelSx={{
      fontWeight: '600',
      fontSize: '16'
    }}
      name={'reasonNotMet'} label='Reason Needs Were Not Met' onChange={handleChange} value={action.reasonNotMet}
    />
    <Box py={'10px'} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      <LabelWithToolTip sx={{
        fontWeight: '600',
        fontSize: '16'
      }}
        labelText='Services Added' toolTipText='Any service that was added by assigned users will appear in this section.'
      />
      <CheckBoxWithLabel labelSx={{
        fontWeight: '600',
        fontSize: '16',
        mt: 0,
        alignContent: 'center'
      }}
        name={'connectedToReferral'} label='Connected to Referral' value={action.connectedToReferral} setValue={connectedToReferralChange} edge={'start'}
      />
    </Box>
    <Box>
      {action.serviceLogs.map((s) => {
        return <Box pt={'10px'}><ServiceLogCard key={s.serviceName} {...s} /></Box>
      })}
    </Box>
  </Box>

  return <AcceptDenyModal
    acceptButtonSx={{ textTransform: 'none' }}
    acceptbuttoncolor={'secondary'}
    maxWidth='sm'
    fullWidth
    open={props.visible}
    titleContent={titleContent}
    dialogContent={content}
    acceptButtonText='End Care Management'
    onClose={handleClose}
    onAccept={handleSubmit}
  />
}
